import { useState, useEffect } from 'react';
import { DatabaseRow } from '@cleanpro/common-ui';
import { supabase } from '@cleanpro/common-ui';
import { Business } from '../../service-providers/business';
import { Service } from '../../services/types';
import { Property } from '../../properties/types';
import { SupabaseCustomerRepository } from '../../customers/repositories/SupabaseCustomerRepository';
import { GetAllCustomersUseCase } from '../../customers/use-cases/GetAllCustomersUseCase';
import { fetchAllServices } from '../../services/api';
import { fetchAllBusinesses } from '../../service-providers/api';
// import { fetchAllProperties } from '../../properties/api';
import { CustomerResponse as Customer } from '../../customers/types';
import { BlockedDate, JobRow } from '../types';
import { fetchAllJobs } from '../api';
import { fetchAllProperties } from '../../properties/api';

/**
 * Custom hook for fetching and managing jobs data.
 * Extracted from JobsContext to keep the provider lean and testable.
 * 
 * This hook encapsulates all data fetching logic and maintains local state
 * for jobs, customers, services, businesses, and blocked dates.
 */
export function useJobsData() {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);

  // Utility function to transform blocked dates
  const transformBlockedDate = (row: DatabaseRow): BlockedDate => ({
    id: row.id as string,
    date: row.date as string,
    reason: row.reason as string || undefined,
    createdBy: row.created_by as string || undefined,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string
  });

  // Fetch all data on mount
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        // Skip auth.getUser when using Clerk - not available with accessToken callback
        // const user = await supabase.auth.getUser();
        // console.log('useJobsData: auth user', user.data.user?.id || 'none');

        const [
          jobsData, 
          customersData, 
          servicesData, 
          businessesData,
          propertiesData
        ] = await Promise.all([
          fetchAllJobs(),
          (async () => {
            const customerRepository = new SupabaseCustomerRepository();
            const getAllCustomersUseCase = new GetAllCustomersUseCase(customerRepository);
            const result = await getAllCustomersUseCase.execute({});
            return result.customers;
          })(),
          fetchAllServices(),
          fetchAllBusinesses(),
          fetchAllProperties()
        ]);

        if (isMounted) {
          const transformedJobs = jobsData.map(job => ({
            ...job,
            recurring_frequency: job.recurring_frequency as "weekly" | "bi-weekly" | "tri-weekly" | "monthly" | null | undefined
          }));
          setJobs(transformedJobs);
          setCustomers(customersData);
          setServices(servicesData);
          setBusinesses(businessesData);
          setProperties(propertiesData);
        }

      } catch (err) {
        if (isMounted) {
          setError('Failed to fetch job data.');
          console.error(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const fetchBlockedDates = async () => {
      const { data, error } = await supabase
        .from('blocked_dates')
        .select('*')
        .order('date', { ascending: true });
      
      if (error) {
        console.error('Blocked dates fetch error:', error);
        if (isMounted) {
          setBlockedDates([]);
        }
        return;
      }
      
      if (isMounted) {
        setBlockedDates(data ? data.map(transformBlockedDate) : []);
      }
    };
    
    fetchData();
    fetchBlockedDates();

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    jobs,
    setJobs,
    loading,
    error,
    blockedDates,
    setBlockedDates,
    customers,
    services,
    businesses,
    properties,
    transformBlockedDate
  };
}
